import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import ReportFilter from "./components/ReportFilter";
import { PaginationPage } from "../../components/PaginationPage";

const PendingPaymentReport = () => {
  const [filters, setFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({});
  // Starts true: the report loads on open, so "No data" never shows before the first answer.
  const [loading, setLoading] = useState(true);

  const fetchReport = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get("/api/reports/pending-payments", {
        params: { ...filters, page },
      });
      const payload = res.data;
      setRows(payload.data || payload);
      setPagination({
        current_page: payload.current_page,
        last_page: payload.last_page,
      });
    } catch (err) {
      console.error("Failed to load pending payment report", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(1);
    // Reason: load once when the page opens; later loads come from the Search button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPDF = async () => {
    try {
      const res = await axios.get("/api/reports/pending-payments/pdf", {
        params: filters,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "pending-payment-report.pdf");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Failed to download PDF", err);
    }
  };

  const downloadCSV = async () => {
    try {
      const res = await axios.get("/api/reports/pending-payments/csv", {
        params: filters,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "pending-payment-report.csv");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Failed to download CSV", err);
    }
  };

  return (
    <div className="container py-4">
      <h4 className="fw-bold mb-3">Pending Payment Report</h4>

      <ReportFilter filters={filters} setFilters={setFilters} onSearch={fetchReport} />

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label">Schedule</label>
              <select
                className="form-select"
                value={filters.schedule || ""}
                onChange={(e) =>
                  setFilters({ ...filters, schedule: e.target.value || undefined })
                }
              >
                <option value="">All</option>
                <option value="Monthly">Monthly</option>
                <option value="Weekly">Weekly</option>
                <option value="15days">15days</option>
              </select>
            </div>
            <div className="col-md-9 d-flex align-items-end justify-content-end gap-2">
              <button className="btn btn-primary" onClick={() => fetchReport(1)}>
                {loading ? "Loading..." : "Search"}
              </button>
              <button className="btn btn-outline-danger" onClick={downloadPDF}>
                PDF Download
              </button>
              <button className="btn btn-outline-secondary" onClick={downloadCSV}>
                CSV Download
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                <th>Property</th>
                <th>Unit</th>
                <th>Total Payable</th>
                <th>Total Paid</th>
                <th>Due</th>
                <th>Next Inst. Amount</th>
                <th>Paid / Left</th>
                <th>Schedule</th>
                <th>Next Due Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    {loading ? "Loading..." : "No data"}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => {
                  const paidCount = Number(row.paid_count || 0);
                  const totalInst = Number(row.total_installments || 0);
                  const left = Math.max(totalInst - paidCount, 0);
                  return (
                    <tr key={row.id || idx}>
                      <td>{row.property?.name || "—"}</td>
                      <td>{row.unit?.unit_number || "—"}</td>
                      <td>{Number(row.total_amount).toFixed(2)}</td>
                      <td>{Number(row.paid_amount).toFixed(2)}</td>
                      <td className="text-danger fw-bold">{Number(row.due_amount).toFixed(2)}</td>
                      <td>{Number(row.next_amount || 0).toFixed(2)}</td>
                      <td>
                        {paidCount} / {left}
                      </td>
                      <td>{row.plan_name || "—"}</td>
                      <td>{row.next_due_date || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <PaginationPage
            pagination={pagination}
            onPageChange={(page) => fetchReport(page)}
          />
        </div>
      </div>
    </div>
  );
};

export default PendingPaymentReport;

